import React from 'react';
import { Accordion, Col, Nav, Placeholder, Row } from 'react-bootstrap';
import { BsReply, BsChatText, BsFileEarmarkText, BsTags, BsClock } from 'react-icons/bs';
import convStyles from './Conversations.module.css';
import commentStyles from './Comment.module.css';
import skelStyles from './TicketDetailsSkeleton.module.css';

function Bone({ width = '100%', height = 14, className = '', block = true }) {
    return (
        <Placeholder as="span" animation="glow" className={`${block ? skelStyles.boneBlock : skelStyles.boneInline} ${className}`} style={block ? { width, display: 'block' } : {}}>
            <span className="placeholder rounded" style={{ display: 'block', width: block ? '100%' : width, height }} />
        </Placeholder>
    );
}

function BtnBone({ width, className = skelStyles.btnBone }) {
    return (
        <div className={className} style={{ width }} aria-hidden="true">
            <Placeholder as="span" animation="glow" className="d-block h-100 w-100">
                <span className="placeholder w-100 h-100 rounded" />
            </Placeholder>
        </div>
    );
}

function AvatarBone() {
    return (
        <Placeholder as="span" animation="glow" className="d-block">
            <span className="placeholder rounded-circle" style={{ width: 50, height: 50, display: 'block' }} />
        </Placeholder>
    );
}

function TicketTitleSkeleton() {
    return (
        <div className={`d-flex flex-column align-items-start ${skelStyles.ticketTitleBlock}`}>
            <Placeholder as="div" animation="glow" className="mb-1 w-100">
                <span className={`placeholder rounded ${skelStyles.ticketTitleLine}`} style={{ width: '38%' }} />
            </Placeholder>
            <Placeholder as="div" animation="glow" className="w-100">
                <span className={`placeholder rounded ${skelStyles.ticketIdLine}`} style={{ width: '28%' }} />
            </Placeholder>
        </div>
    );
}

function PropertyField({ label }) {
    return (
        <div className={skelStyles.propertyField}>
            <div className={`${skelStyles.propertyLabel} mb-1`}>
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>{label}:</span>
            </div>
            <Bone height={38} className={skelStyles.selectBone} />
        </div>
    );
}

function MessageSkeleton({ role = 'customer', type = 'reply', titleLines = 3 }) {
    return (
        <div className={convStyles.boxContainer} data-role={role} data-type={type}>
            <div className={convStyles.header}>
                <div className={convStyles.user}>
                    <AvatarBone />
                    <Bone width={56} height={10} className="mt-1" />
                </div>
                <div className={convStyles.wrapper}>
                    <div className={convStyles.contentContainer}>
                        {Array.from({ length: titleLines }).map((_, i) => (
                            <Bone
                                key={i}
                                width={i === titleLines - 1 ? '62%' : '92%'}
                                height={12}
                                className={i < titleLines - 1 ? 'mb-2' : ''}
                            />
                        ))}
                    </div>
                    <div className={`${convStyles.date} ${skelStyles.dateBone}`}>
                        <Bone width={110} height={10} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function TicketHeaderSkeleton() {
    return (
        <div className={convStyles.container} data-type="ticket">
            <div className={convStyles.boxContainer}>
                <div className={convStyles.header}>
                    <div className={convStyles.user}>
                        <AvatarBone />
                        <Bone width={56} height={10} className="mt-1" />
                    </div>
                    <div className={convStyles.wrapper}>
                        <TicketTitleSkeleton />
                        <div className={convStyles.wrapper}>
                            <div className={convStyles.contentContainer}>
                                <Bone width="40%" height={12} />
                            </div>
                            <div className={`${convStyles.date} ${skelStyles.dateBone}`}>
                                <Bone width={130} height={10} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ReplyEditorSkeleton() {
    return (
        <div className={`${commentStyles.container} ${commentStyles.expanded}`}>
            <div className={commentStyles.header}>
                <span className="text-muted">To:</span>
                <Bone width={180} height={14} className="ms-2" block={false} />
            </div>
            <div className={`${commentStyles.header} mt-1`}>
                <span className="text-muted">CC:</span>
                <Bone width={120} height={14} className="ms-2" block={false} />
            </div>
            <div className={skelStyles.editorArea}>
                <Bone width="40%" height={12} />
            </div>
            <div className={`d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2 ${commentStyles.commentActions}`}>
                <BtnBone width={90} />
                <BtnBone width={108} className={skelStyles.btnBonePrimary} />
            </div>
        </div>
    );
}

function TabNav({ children, className = '' }) {
    return (
        <Nav variant="tabs" className={`position-relative ${className}`}>
            {children}
        </Nav>
    );
}

export default function TicketDetailsSkeleton({ showSidebar = true }) {
    return (
        <Row className="py-3" style={{ marginRight: 0 }}>
            <Col sm={12} md={8} xxl={9}>
                {showSidebar && (
                    <div className={skelStyles.actions}>
                        <BtnBone width={148} />
                        <BtnBone width={148} />
                        <BtnBone width={118} />
                        <BtnBone width={31} />
                    </div>
                )}

                <TicketHeaderSkeleton />

                <TabNav>
                    <Nav.Item>
                        <Nav.Link active disabled className="d-flex align-items-center gap-1">
                            <BsReply size={15} /> Reply
                        </Nav.Link>
                    </Nav.Item>
                    {showSidebar && (
                        <>
                            <Nav.Item>
                                <Nav.Link disabled className="d-flex align-items-center gap-1">
                                    <BsReply size={15} className="flip-icon" /> Forward
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link disabled className="d-flex align-items-center gap-1">
                                    <BsFileEarmarkText size={15} /> Add Note
                                </Nav.Link>
                            </Nav.Item>
                        </>
                    )}
                </TabNav>

                <ReplyEditorSkeleton />

                <TabNav className="mt-3">
                    <Nav.Item>
                        <Nav.Link active={!showSidebar} disabled className="d-flex align-items-center gap-1">
                            <BsChatText size={15} /> Conversations
                        </Nav.Link>
                    </Nav.Item>
                    {showSidebar && (
                        <>
                            <Nav.Item>
                                <Nav.Link active disabled className="d-flex align-items-center gap-1">
                                    <BsFileEarmarkText size={15} /> Notes
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link disabled className="d-flex align-items-center gap-1">
                                    <BsFileEarmarkText size={15} /> Claritask Comments
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link disabled className={skelStyles.claritaskTabLabel}>
                                    <img src="/images/claritasklogo.svg" alt="" style={{ width: 14, height: 14, opacity: 0.5 }} />
                                    Linked Claritask
                                </Nav.Link>
                            </Nav.Item>
                        </>
                    )}
                </TabNav>

                <div className={convStyles.container}>
                    {showSidebar ? (
                        <>
                            <MessageSkeleton role="agent" type="note" titleLines={2} />
                            <MessageSkeleton role="agent" type="note" titleLines={3} />
                        </>
                    ) : (
                        <>
                            <MessageSkeleton role="customer" type="reply" titleLines={2} />
                            <MessageSkeleton role="agent" type="reply" titleLines={3} />
                        </>
                    )}
                </div>
            </Col>

            {showSidebar && (
                <Col sm={12} md={4} xxl={3} style={{ paddingLeft: '12px' }}>
                    <Accordion defaultActiveKey={['0', '1']} alwaysOpen>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>
                                <BsTags className="me-2" size={18} /> Ticket Properties
                            </Accordion.Header>
                            <Accordion.Body className="text-start ticket-properties">
                                <PropertyField label="Due Date" />
                                <PropertyField label="Assignees" />
                                <PropertyField label="Status" />
                                <PropertyField label="Department" />
                                <PropertyField label="Priority" />
                                <PropertyField label="Type" />
                                <PropertyField label="Tags" />
                            </Accordion.Body>
                        </Accordion.Item>
                        <Accordion.Item eventKey="1">
                            <Accordion.Header>
                                <BsClock className="me-2" size={18} /> Time Logs
                            </Accordion.Header>
                            <Accordion.Body>
                                <Bone height={36} className={`${skelStyles.timerBone} mb-3`} />
                                <Bone width="55%" height={12} className="mb-2" />
                                <Bone width="70%" height={12} />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </Col>
            )}
        </Row>
    );
}
